import { Controller } from "@hotwired/stimulus"

// Phase 3.8 Tier 3 Step 40 — Microsoft OneDrive / SharePoint folder
// picker (File Picker v8).
//
// File Picker v8 communicates via postMessage between an iframe (or
// popup) and the host page. There's no SDK to load; we open the picker
// host URL in a popup, perform a small handshake (`initialize` →
// `activate` → `command:authenticate` for AccessToken auth), and listen
// for `command:pick` messages which carry the selected item(s).
//
// Reference: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/v8-schema
//
// Why postMessage and not iframe-mount? File Picker v8 expects to be
// loaded from `onedrive.live.com/picker` (consumer) or
// `{tenant}-my.sharepoint.com/_layouts/15/FilePicker.aspx` (business).
// The picker page is an Angular app served by Microsoft; embedding it
// inline is not supported (X-Frame-Options blocks it). The popup +
// postMessage pattern is the canonical embed.
export default class extends Controller {
  static values = {
    pickerUrl:   String,  // e.g. "https://onedrive.live.com/picker"
    accessToken: String,
    channelId:   String,
    origin:      String   // our origin, sent in the initialize payload
  }

  static targets = ["display", "folderIdField", "folderNameField", "folderUrlField"]

  connect() {
    this._messageHandler = (event) => this._handleMessage(event)
    window.addEventListener("message", this._messageHandler)
  }

  disconnect() {
    if (this._messageHandler) {
      window.removeEventListener("message", this._messageHandler)
    }
    this._closePopup()
  }

  open(event) {
    event.preventDefault()
    if (!this.accessTokenValue) {
      this._setStatus("Reconnect OneDrive to pick a folder — the OAuth token is missing.")
      return
    }
    this._launchPopup()
  }

  _launchPopup() {
    // Picker v8's options ride on the URL fragment (`#`) rather than
    // querystring; this matches Microsoft's first-party embedders and
    // avoids the picker host re-routing on page reload.
    const options = {
      sdk: "8.0",
      entry: { sharePoint: {} },
      authentication: {},
      messaging: {
        origin: this.originValue || window.location.origin,
        channelId: this.channelIdValue || crypto.randomUUID()
      },
      typesAndSources: {
        mode: "folders",
        pivots: { oneDrive: true, recent: true, sharedLibraries: true }
      },
      selection: { mode: "single" },
      theme: "light"
    }

    const url = `${this.pickerUrlValue}?filePicker=${encodeURIComponent(JSON.stringify(options))}`
    this._popup = window.open(url, "onedrive-picker", "width=1080,height=720,resizable=yes,scrollbars=yes")
    if (!this._popup) {
      this._setStatus("Pop-up blocked — allow pop-ups for ConsLog and try again.")
      return
    }
    // Channel id we'll match against incoming messages so two pickers
    // open simultaneously can't cross-wire each other's events.
    this._channelId = options.messaging.channelId
  }

  _handleMessage(event) {
    if (!this._popup) return
    // Canonical postMessage origin check — parsed URL.origin equality.
    // The earlier `pickerUrlValue.startsWith(event.origin)` was correct
    // for the standard case but case-sensitive and substring-based,
    // which is fragile. URL.origin normalizes scheme + host + port and
    // strips path, giving the safe comparison shape every postMessage
    // handler should use.
    let pickerOrigin = ""
    try { pickerOrigin = new URL(this.pickerUrlValue).origin } catch (_e) { return }
    if (event.origin !== pickerOrigin) return
    const data = event.data || {}
    if (data.channelId && this._channelId && data.channelId !== this._channelId) return

    // The picker's first message after window load is `initialize` —
    // we acknowledge by posting `activate` back so the picker proceeds
    // past the splash. Microsoft refers to this as the v8 handshake.
    if (data.type === "initialize") {
      this._postToPicker({ type: "activate" })
      return
    }

    // Auth requests come through as `command` messages with type
    // `authenticate`. We respond with the access token; the picker
    // uses it for Graph calls in its own iframe.
    if (data.type === "command" && data.data && data.data.command === "authenticate") {
      this._postToPicker({
        type: "result",
        id: data.data.id,
        data: { type: "token", token: this.accessTokenValue }
      })
      return
    }

    // The pick result. Items come through as an array; folder mode
    // limits selection to one item per the options we sent.
    if (data.type === "command" && data.data && data.data.command === "pick") {
      const items = data.data.items || []
      const folder = items[0]
      if (folder) {
        this._handlePicked(folder)
      }
      this._postToPicker({ type: "result", id: data.data.id, data: { result: "success" } })
      this._closePopup()
      return
    }

    // Picker close without selection — just clean up.
    if (data.type === "command" && data.data && data.data.command === "close") {
      this._postToPicker({ type: "result", id: data.data.id, data: { result: "success" } })
      this._closePopup()
    }
  }

  _handlePicked(folder) {
    // File Picker v8 pick result shape:
    //   { id, name, webUrl, parentReference: { driveId, ... }, ... }
    // We persist `id` (the driveItem id), `name` (display label), and
    // `webUrl` (the SharePoint / OneDrive deep link).
    const id = folder.id || ""
    const name = folder.name || ""
    const webUrl = folder.webUrl || folder.webDavUrl || ""

    if (this.hasFolderIdFieldTarget)   this.folderIdFieldTarget.value   = id
    if (this.hasFolderNameFieldTarget) this.folderNameFieldTarget.value = name
    if (this.hasFolderUrlFieldTarget)  this.folderUrlFieldTarget.value  = webUrl
    if (this.hasDisplayTarget)         this.displayTarget.value         = name
  }

  _postToPicker(message) {
    if (!this._popup || this._popup.closed) return
    this._popup.postMessage(message, this.pickerUrlValue)
  }

  _closePopup() {
    if (this._popup && !this._popup.closed) {
      this._popup.close()
    }
    this._popup = null
    this._channelId = null
  }

  _setStatus(message) {
    if (this.hasDisplayTarget) {
      this.displayTarget.value = message
    } else {
      console.warn("[onedrive-folder-picker]", message)
    }
  }
}
